/**
 * Which session authorizes Tractor Metrics, chosen the same way as Tractor
 * State's route (see TractorStateRoute for the history): exactly one OIDC
 * provider on this route. On the Cognito path the app session already is a
 * fleet_state session; on the Auth0 path the page-scoped Cognito login
 * supplies the token the activity routes need.
 *
 * There is no offline mode: the page has no snapshot to show, so an
 * unconfigured build says so.
 */
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import RequireAuth from "../../components/RequireAuth/RequireAuth";
import RequirePermission from "../../components/RequirePermission/RequirePermission";
import RequireFleetStateAuth from "../../components/RequireFleetStateAuth/RequireFleetStateAuth";
import {
  FleetStateAuthProvider,
  FleetStateNotConfigured,
  useFleetStateAuth,
} from "../../auth/fleetStateAuth";
import { useFleetAuth } from "../../auth/fleetAuth";
import {
  AUTH_PROVIDER,
  FLEET_STATE_CONFIGURED,
  TRACTOR_METRICS_ENABLED,
} from "../../utils/devFlags";
import TractorMetrics from "./TractorMetrics";

export default function TractorMetricsRoute() {
  if (!TRACTOR_METRICS_ENABLED) return <Navigate to="/dashboard" replace />;
  if (!FLEET_STATE_CONFIGURED) return <FleetStateNotConfigured />;
  return (
    <RequireAuth>
      <RequirePermission permission="read:aerovect-vehicles">
        {AUTH_PROVIDER === "cognito" ? (
          <AppSession />
        ) : (
          <FleetStateAuthProvider>
            <RequireFleetStateAuth>
              <PageScopedSession />
            </RequireFleetStateAuth>
          </FleetStateAuthProvider>
        )}
      </RequirePermission>
    </RequireAuth>
  );
}

/** The app-wide Cognito session's access token, read once. */
function AppSession() {
  const { getAccessToken } = useFleetAuth();
  const [token, setToken] = useState<string | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getAccessToken().then(
      (t) => !cancelled && setToken(t),
      () => !cancelled && setFailed(true),
    );
    return () => {
      cancelled = true;
    };
  }, [getAccessToken]);
  if (failed) {
    return (
      <p style={{ padding: 24 }}>
        Couldn’t read your session token. Reload the page, or sign out and back
        in.
      </p>
    );
  }
  if (!token) return <p style={{ padding: 24 }}>Loading…</p>;
  return <TractorMetrics token={token} />;
}

/** The page-scoped Cognito session's ACCESS token (not the id token). */
function PageScopedSession() {
  const auth = useFleetStateAuth();
  return <TractorMetrics token={auth.user?.access_token} />;
}
